import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersService } from '@/services/usersService';
import type { AdminUserFilters, AdminUser, AdminUserListResponse } from '@/types/admin.types';

/**
 * Hook to fetch paginated users list with filters
 */
export function useUsers(filters?: AdminUserFilters) {
  return useQuery({
    queryKey: ['admin', 'users', filters],
    queryFn: () => usersService.getUsers(filters),
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Hook to fetch a single user's details
 * Falls back to finding user in list cache if detail endpoint fails
 */
export function useUser(userId: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['admin', 'users', userId],
    queryFn: async () => {
      // Try to get user from detail endpoint
      const user = await usersService.getUser(userId);
      if (user) return user;

      // Fallback: try to find user in cached list data
      const listData = queryClient.getQueriesData<AdminUserListResponse>({
        queryKey: ['admin', 'users'],
      });

      for (const [, data] of listData) {
        const cachedUser = data?.users?.find(u => u.id === userId);
        if (cachedUser) {
          return cachedUser;
        }
      }

      return null;
    },
    enabled: !!userId,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Hook to update a user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: Partial<AdminUser> }) =>
      usersService.updateUser(userId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
}

/**
 * Hook to change user's plan
 */
export function useChangePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, planId }: { userId: string; planId: string }) =>
      usersService.changePlan(userId, planId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
}

/**
 * Hook to cancel user's subscription
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, immediate }: { userId: string; immediate?: boolean }) =>
      usersService.cancelSubscription(userId, immediate),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
}

/**
 * Hook to extend user's trial
 */
export function useExtendTrial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, days }: { userId: string; days: number }) =>
      usersService.extendTrial(userId, days),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
}

/**
 * Hook to get user's usage history
 */
export function useUserUsage(userId: string, months?: number) {
  return useQuery({
    queryKey: ['admin', 'users', userId, 'usage', months],
    queryFn: () => usersService.getUserUsage(userId, months),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get user's billing history
 */
export function useUserBilling(userId: string) {
  return useQuery({
    queryKey: ['admin', 'users', userId, 'billing'],
    queryFn: () => usersService.getUserBilling(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to export users
 */
export function useExportUsers() {
  return useMutation({
    mutationFn: (filters?: AdminUserFilters) => usersService.exportUsers(filters),
    onSuccess: (blob) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}
